import * as React from 'react'
import { useMemo } from 'react'
import {
  useActions,
  useData,
  getStyles,
} from '@user/components/Contacts/ContactCard/hooks'
import Box from '@mui/material/Box'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import { MoreButton, PhoneLink } from '@sales-center/components'
import { LocationLink } from '@user/components/Location'
import { CardVariants } from '@user/components/Contacts/ContactCard'
import { ContactCardProps } from '@user/components/Contacts/ContactCard/typings'
import type { PopoverOrigin } from '@mui/material/Popover'
import { AvatarStyled } from '@user/features/EntityDetails/components/AvatarStyled'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { IContact } from '@sales-center/common'
import { ToTitleCase } from '@user/utils/ToTitleCase'
import { useTheme } from '@mui/material/styles'

const popoverConfig: PopoverOrigin = {
  vertical: 'top',
  horizontal: 'right',
}

type EntityDetailsContactCardProps = Omit<
  ContactCardProps,
  'hideEditOptions'
> & {
  hidePrimaryRadio?: boolean
  primaryContactData?: IContact | null
}

export function EntityDetailsContactCard(props: EntityDetailsContactCardProps) {
  const {
    contact,
    isPrimaryContact,
    variant,
    canUpdateEntity,
    primaryContactData = null,
    hidePrimaryRadio = false,
  } = props

  const {
    locationCode,
    externalLinkId,
    externalLinkType,
    company,
    phoneNumber,
    phoneExt,
    contactMethod,
    altPhoneNumber,
    altPhoneExt,
  } = contact

  const { onChangePrimaryContact } = useActions(props)

  const {
    contactName,
    contactTimeFormatted,
    contactTypeLabel,
    formattedAddress,
    primaryEmail,
    secondaryEmails,
    truncatedName,
    cardBG,
    showMoreButton,
    menuOptions,
  } = useData(props)

  const showRadio = canUpdateEntity && !hidePrimaryRadio
  const styles = getStyles(props)
  const contactMethodData = useMemo(() => {
    if (contactMethod) {
      return contactMethod
    }
    if (!contactMethod && isPrimaryContact) {
      return primaryContactData?.contactMethod
    }
  }, [contactMethod, isPrimaryContact, primaryContactData])

  const contactTimeData = useMemo(() => {
    if (contactTimeFormatted) {
      return contactTimeFormatted
    }
    if (!contactTimeFormatted && isPrimaryContact) {
      return (
        primaryContactData?.contactTime &&
        primaryContactData?.contactTime
          .split(',')
          .map((x) => ToTitleCase(x))
          .join(', ')
      )
    }
  }, [contactTimeFormatted, isPrimaryContact, primaryContactData?.contactTime])
  const theme = useTheme()
  return (
    <Stack
      borderRadius="4px"
      boxShadow="0px 0px 0px 1px #E0E0E0"
      pt={2.4}
      pb={2}
      px={2}
      bgcolor={cardBG}
    >
      <Stack direction="row" alignItems="center">
        <AvatarStyled>{truncatedName}</AvatarStyled>
        <Stack>
          <Typography variant="h6" color={theme.palette.text.primary}>
            {contactName}
          </Typography>
          {contactTypeLabel && (
            <Typography variant="body2" color={theme.palette.text.primary}>
              {contactTypeLabel} - {company}
            </Typography>
          )}
        </Stack>
        <Stack ml="auto">
          {showMoreButton && (
            <MoreButton
              anchorOrigin={popoverConfig}
              transformOrigin={popoverConfig}
              options={menuOptions || []}
              dataTestId="showMoreContactBtn"
            />
          )}
        </Stack>
      </Stack>
      <Stack direction="row" mt={2.2} flexWrap="wrap">
        {contactTypeLabel && !!externalLinkId && (
          <Box mr={3}>
            <Typography variant="body2" color={theme.palette.text.secondary}>
              {contactTypeLabel}
            </Typography>
            <LocationLink
              externalLocationId={externalLinkId}
              externalLocationCode={locationCode}
              hideLocationPrefix
              billingLocation={externalLinkType === 'BillTo'}
            />
          </Box>
        )}

        {primaryEmail && (
          <Box mr={3}>
            <Typography variant="body2" color={theme.palette.text.secondary}>
              Email
            </Typography>
            <a href={`mailto:${primaryEmail}`} target="_blank" rel="noreferrer">
              <Typography
                variant="body2"
                color="#1565c0"
                fontFamily="Source Sans Pro Semibold"
              >
                {primaryEmail}
              </Typography>
            </a>
          </Box>
        )}
        {phoneNumber && (
          <Box mr={3}>
            <Typography variant="body2" color={theme.palette.text.secondary}>
              Main Phone
            </Typography>
            <PhoneLink
              phone={phoneNumber}
              phoneExt={phoneExt}
              includeExtInLink
            />
          </Box>
        )}
        {formattedAddress && (
          <Box mr={3}>
            <Typography variant="body2" color={theme.palette.text.secondary}>
              Address
            </Typography>
            <Typography variant="body2" color={theme.palette.text.primary}>
              {formattedAddress}
            </Typography>
          </Box>
        )}
        {altPhoneNumber && (
          <Box mr={3}>
            <Typography variant="body2" color={theme.palette.text.secondary}>
              Alt. Phone
            </Typography>
            <PhoneLink
              phone={altPhoneNumber}
              phoneExt={altPhoneExt}
              includeExtInLink={true}
            />
          </Box>
        )}
        {contactMethodData && variant !== CardVariants.wwm && (
          <Box mr={3}>
            <Typography variant="body2" color={theme.palette.text.secondary}>
              Best Contact Method
            </Typography>
            <Typography variant="subtitle2" color={theme.palette.text.primary}>
              {contactMethodData || <>-</>}
            </Typography>
          </Box>
        )}
        {contactTimeData && variant !== CardVariants.wwm && (
          <Box mr={3}>
            <Typography variant="body2" color={theme.palette.text.secondary}>
              Best Contact Time
            </Typography>
            <Typography variant="subtitle2" color={theme.palette.text.primary}>
              {contactTimeData || <>-</>}
            </Typography>
          </Box>
        )}
        {secondaryEmails &&
          secondaryEmails.map((email, index) => (
            <Box key={`secondary_email_${index}`} mr={3}>
              <Typography variant="body2" color={theme.palette.text.secondary}>
                Secondary email
              </Typography>
              <Typography
                variant="subtitle2"
                color={theme.palette.text.primary}
              >
                <a
                  href={`mailto:${email.emailAddress}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {email.emailAddress}
                </a>
              </Typography>
            </Box>
          ))}
        {variant === CardVariants.wwm && (
          <>
            <Box mr={3}>
              <Typography variant="body2" color={theme.palette.text.secondary}>
                Best Contact Method
              </Typography>
              <Typography
                variant="subtitle2"
                color={theme.palette.text.primary}
              >
                {contactMethod || <>-</>}
              </Typography>
            </Box>
            <Box mr={3}>
              <Typography variant="body2" color={theme.palette.text.secondary}>
                Best Contact Time
              </Typography>
              <Typography
                variant="subtitle2"
                color={theme.palette.text.primary}
              >
                {contactTimeFormatted || <>-</>}
              </Typography>
            </Box>
          </>
        )}
      </Stack>
      <Stack>
        {showRadio && (
          <Box mt={3}>
            <FormControlLabel
              sx={styles.primaryContactFormLabel}
              checked={isPrimaryContact}
              value={true}
              control={<Radio size="small" />}
              label="Primary Contact"
              labelPlacement="end"
              onChange={onChangePrimaryContact}
              data-test-id="primaryContactRadio"
            />
          </Box>
        )}
      </Stack>
    </Stack>
  )
}
